import { useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FloatingContact from "../components/FloatingContact";
import SEOHead from "../components/SEOHead";
import { useLanguage } from "../contexts/LanguageContext";
import {
    getLocalizedSolutionLanding,
    SOLUTION_SLUGS,
    SOLUTION_LANDINGS,
} from "../data/solutionLandings";
import { Button } from "../components/ui/button";
import { Check, ArrowRight } from "lucide-react";
import { getExperimentVariant } from "../lib/abTesting";
import { sendFunnelEvent } from "../lib/funnelEvents";

const SITE_URL = "https://adrise-agency.com";

export default function SolutionLandingPage() {
    const { slug } = useParams();
    const { language } = useLanguage();
    const entry = getLocalizedSolutionLanding(slug, language);
    const ctaVariant = useMemo(
        () => getExperimentVariant("solution_cta_copy", ["A", "B"]),
        [],
    );
    const safeEntry =
        entry ||
        ({
            title: "",
            subtitle: "",
            seoDescription: "",
            bullets: [],
            faq: [],
            image: "",
        });

    const c = {
        en: {
            eyebrow: "Solutions",
            included: "What is included",
            faq: "Frequently asked questions",
            ctaTitle: "Ready to activate this setup?",
            ctaBody:
                "Apply to Adrise and get your operational path aligned within 24 hours.",
            ctaPrimary: "Apply for Access",
            ctaSecondary: "See pricing",
            other: "Other solution pages",
        },
        pt: {
            eyebrow: "Soluções",
            included: "O que está incluído",
            faq: "Perguntas frequentes",
            ctaTitle: "Pronto para ativar este setup?",
            ctaBody:
                "Solicite acesso à Adrise e alinhe seu caminho operacional em até 24h.",
            ctaPrimary: "Solicitar acesso",
            ctaSecondary: "Ver preços",
            other: "Outras páginas de solução",
        },
        es: {
            eyebrow: "Soluciones",
            included: "Qué incluye",
            faq: "Preguntas frecuentes",
            ctaTitle: "¿Listo para activar este setup?",
            ctaBody:
                "Solicita acceso a Adrise y alinea tu ruta operativa en 24h.",
            ctaPrimary: "Solicitar acceso",
            ctaSecondary: "Ver precios",
            other: "Otras páginas de solución",
        },
        ru: {
            eyebrow: "Решения",
            included: "Что входит",
            faq: "Частые вопросы",
            ctaTitle: "Готовы активировать этот setup?",
            ctaBody:
                "Подайте заявку в Adrise и выстройте операционный путь за 24 часа.",
            ctaPrimary: "Подать заявку",
            ctaSecondary: "Смотреть тарифы",
            other: "Другие solution-страницы",
        },
        tr: {
            eyebrow: "Çözümler",
            included: "Neler dahil",
            faq: "Sık sorulan sorular",
            ctaTitle: "Bu setup’ı aktive etmeye hazır mısınız?",
            ctaBody:
                "Adrise başvurunuzu yapın, operasyon yolunuzu 24 saat içinde netleştirelim.",
            ctaPrimary: "Erişim Başvurusu",
            ctaSecondary: "Fiyatları gör",
            other: "Diğer çözüm sayfaları",
        },
    }[language] || {
        eyebrow: "Solutions",
        included: "What is included",
        faq: "Frequently asked questions",
        ctaTitle: "Ready to activate this setup?",
        ctaBody: "Apply to Adrise and get your operational path aligned within 24 hours.",
        ctaPrimary: "Apply for Access",
        ctaSecondary: "See pricing",
        other: "Other solution pages",
    };

    const ctaByVariant = {
        A: {
            en: "Apply for Access",
            pt: "Solicitar acesso",
            es: "Solicitar acceso",
            ru: "Подать заявку",
            tr: "Erişim Başvurusu",
        },
        B: {
            en: "Get your setup plan",
            pt: "Receber plano de setup",
            es: "Obtener plan de setup",
            ru: "Получить setup-план",
            tr: "Setup planını al",
        },
    };

    const jsonLd = useMemo(
        () => [
            {
                "@context": "https://schema.org",
                "@type": "Service",
                name: safeEntry.title,
                description: safeEntry.seoDescription,
                provider: {
                    "@type": "Organization",
                    name: "Adrise",
                    url: SITE_URL,
                },
                areaServed: "Worldwide",
                url: `${SITE_URL}/solutions/${slug}`,
            },
            {
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: (safeEntry.faq || []).slice(0, 3).map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: f.a,
                    },
                })),
            },
            {
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                    {
                        "@type": "ListItem",
                        position: 1,
                        name: "Home",
                        item: SITE_URL,
                    },
                    {
                        "@type": "ListItem",
                        position: 2,
                        name: "Solutions",
                        item: `${SITE_URL}/solutions`,
                    },
                    {
                        "@type": "ListItem",
                        position: 3,
                        name: safeEntry.title,
                        item: `${SITE_URL}/solutions/${slug}`,
                    },
                ],
            },
        ],
        [safeEntry, slug],
    );

    if (!entry) {
        return <Navigate to="/" replace />;
    }

    return (
        <main
            data-testid={`solution-landing-${slug}`}
            className="bg-background text-foreground min-h-screen"
        >
            <SEOHead
                title={`${entry.title} | Adrise`}
                description={entry.seoDescription}
                canonical={`${SITE_URL}/solutions/${slug}`}
                image={entry.image}
                keywords={[
                    entry.title.toLowerCase(),
                    "adrise",
                    "agency ad accounts",
                    "credit line",
                ]}
                jsonLd={jsonLd}
            />

            <Navbar />

            <section className="pt-32 md:pt-40 pb-14 md:pb-16" data-testid="solution-hero">
                <div className="container-skala grid lg:grid-cols-12 gap-8 items-center">
                    <div className="lg:col-span-7 space-y-5">
                        <p className="text-[11px] font-mono uppercase tracking-[0.28em] text-primary">
                            {c.eyebrow}
                        </p>
                        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
                            {entry.title}
                        </h1>
                        <p className="text-sm md:text-base text-muted-foreground max-w-2xl leading-relaxed">
                            {entry.subtitle}
                        </p>
                        <div className="flex flex-wrap gap-3 pt-2">
                            <Link to="/signup" data-testid="solution-cta-apply">
                                <Button
                                    className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-11 px-6"
                                    onClick={() => {
                                        sendFunnelEvent("qualified_lead", {
                                            content_name: `solution_cta_${slug}`,
                                            content_category: "solution_page",
                                            page_path: `/solutions/${slug}`,
                                            page_title: `${entry.title} | Adrise`,
                                            value: 1,
                                            currency: "USD",
                                            cta_variant: ctaVariant,
                                        });
                                    }}
                                >
                                    {(ctaByVariant[ctaVariant]?.[language] ||
                                        ctaByVariant[ctaVariant]?.en ||
                                        c.ctaPrimary)}
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            </Link>
                            <Link to="/#pricing" data-testid="solution-cta-pricing">
                                <Button
                                    variant="outline"
                                    className="border-white/15 bg-white/[0.02] h-11 px-6"
                                >
                                    {c.ctaSecondary}
                                </Button>
                            </Link>
                        </div>
                    </div>

                    <div className="lg:col-span-5">
                        <div className="rounded-2xl overflow-hidden border border-white/10 bg-card/30" data-testid="solution-hero-image-wrap">
                            <div className="aspect-[16/10]">
                                <img
                                    src={entry.image}
                                    alt={entry.title}
                                    data-testid="solution-hero-image"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-16 border-y border-white/5 bg-white/[0.01]" data-testid="solution-bullets-section">
                <div className="container-skala">
                    <h2 className="text-base md:text-lg font-semibold mb-6">{c.included}</h2>
                    <div className="grid md:grid-cols-2 gap-4">
                        {entry.bullets.map((item, idx) => (
                            <article
                                key={item}
                                className="rounded-2xl border border-white/[0.08] bg-card p-5"
                                data-testid={`solution-bullet-${idx + 1}`}
                            >
                                <p className="text-sm md:text-base text-foreground/90 leading-relaxed inline-flex gap-2 items-start">
                                    <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                                    <span>{item}</span>
                                </p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-16" data-testid="solution-faq-section">
                <div className="container-skala max-w-4xl space-y-4">
                    <h2 className="text-base md:text-lg font-semibold">{c.faq}</h2>
                    {entry.faq.map((f, idx) => (
                        <article
                            key={f.q}
                            className="rounded-2xl border border-white/[0.08] bg-card p-6"
                            data-testid={`solution-faq-${idx + 1}`}
                        >
                            <h3 className="font-semibold text-base">{f.q}</h3>
                            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                                {f.a}
                            </p>
                        </article>
                    ))}
                </div>
            </section>

            <section className="pb-20" data-testid="solution-crosslinks-section">
                <div className="container-skala">
                    <div className="rounded-2xl border border-white/[0.08] bg-card p-6 md:p-8">
                        <h2 className="text-base md:text-lg font-semibold mb-4">{c.other}</h2>
                        <div className="flex flex-wrap gap-2">
                            {SOLUTION_SLUGS.filter((s) => s !== slug).map((s) => (
                                <Link
                                    key={s}
                                    to={`/solutions/${s}`}
                                    data-testid={`solution-crosslink-${s}`}
                                    className="text-xs md:text-sm px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.02] hover:border-primary/40 hover:text-primary transition-colors"
                                >
                                    {SOLUTION_LANDINGS[s]?.title?.[language] || SOLUTION_LANDINGS[s]?.title?.en || s.replaceAll("-", " ")}
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <FloatingContact />
            <Footer />
        </main>
    );
}
